import * as React from 'react';
import type {
  Nullable,
  AnnotatedJsonSchema,
  DomainEntity,
} from '@web-config-app/core';
import { EntityLoader } from '@web-config-app/core-react';
import { getEntityInstanceName } from '@web-config-app/core';
import type {
  EntitySelectControlSchema,
  PrimitiveObjectArrayItemProps,
} from '@web-config-app/entity-form';
import { isRelationshipEntityReference } from '@web-config-app/schema-utils';
import {
  useEntityReferenceSchemaType,
  useReferencedEntityData,
  useEntityDetailsProps,
  useEntityRelationshipSchema,
} from '@web-config-app/entity-form';
import { PrimitiveObjectArrayItem } from './primitive-object-array-item.component';

export interface PrimitiveObjectArrayItemWithEntityReferenceProps
  extends PrimitiveObjectArrayItemProps {
  children?: React.ReactNode;
  entityReferencePropertySchema: Nullable<AnnotatedJsonSchema>;
  referenceInstanceId?: string;
}

/**
 * Special wrapper around the array of primitive object items when the item contains a property
 * that is an entity reference. This wrapper allows us to retrieve the entity's name and display it
 * as the item's label
 */

const PrimitiveObjectArrayItemWithEntityRelationshipWrapper = ({
  entityReferencePropertySchema: schema,
  referenceInstanceId: instanceId,
  referencedEntity,
  ...props
}: PrimitiveObjectArrayItemWithEntityReferenceProps & {
  referencedEntity: DomainEntity;
}) => {
  const { itemLabel, handleRemoveItem, path, index, hasError } = props;
  const entityReferenceSchema = schema as EntitySelectControlSchema;
  const {
    removeRelationshipItem,
    relationshipDataSchemaType,
    value: relationshipData,
  } = useEntityRelationshipSchema({
    schema: entityReferenceSchema,
  });

  const { currentAuthoringEnvironment } = useEntityDetailsProps();

  const { referencedEntity: entityReferenceData } = useReferencedEntityData({
    domainEntity: referencedEntity,
    instanceId,
    environment: currentAuthoringEnvironment,
    isRelationshipReference: true,
  });

  const handleRemoveEntityReference = () => {
    handleRemoveItem(path, index)();

    if (entityReferenceData) {
      removeRelationshipItem(entityReferenceData.id);
    }
  };

  const itemLabelWithEntityTitle = entityReferenceData
    ? getEntityInstanceName(entityReferenceData, referencedEntity)
    : itemLabel;

  const isMissingRelationshipError =
    relationshipDataSchemaType === 'array' &&
    typeof entityReferenceData?.id === 'string' &&
    ((Array.isArray(relationshipData) &&
      typeof relationshipData.find((item: any) => item.id === instanceId) ===
        'undefined') ||
      typeof relationshipData === 'undefined');

  return (
    <PrimitiveObjectArrayItem
      {...props}
      handleRemoveItem={() => handleRemoveEntityReference}
      itemLabel={itemLabelWithEntityTitle}
      hasError={isMissingRelationshipError || hasError}
    />
  );
};

const PrimitiveObjectArrayItemWithEntityReferenceWrapper = ({
  entityReferencePropertySchema: schema,
  referenceInstanceId: instanceId,
  referencedEntity,
  ...props
}: PrimitiveObjectArrayItemWithEntityReferenceProps & {
  referencedEntity: DomainEntity;
}) => {
  const { itemLabel, handleRemoveItem, path, index } = props;

  const { currentAuthoringEnvironment } = useEntityDetailsProps();

  const { referencedEntity: entityReferenceData } = useReferencedEntityData({
    domainEntity: referencedEntity,
    instanceId,
    environment: currentAuthoringEnvironment,
    isRelationshipReference: false,
  });

  const handleRemoveEntityReference = () => {
    handleRemoveItem(path, index)();
  };

  const itemLabelWithEntityTitle = entityReferenceData
    ? getEntityInstanceName(entityReferenceData, referencedEntity)
    : itemLabel;

  return (
    <PrimitiveObjectArrayItem
      {...props}
      handleRemoveItem={() => handleRemoveEntityReference}
      itemLabel={itemLabelWithEntityTitle}
    />
  );
};

export const PrimitiveObjectArrayItemWithEntityReference = (
  props: PrimitiveObjectArrayItemWithEntityReferenceProps,
) => {
  const entitySelectSchema =
    // eslint-disable-next-line react/destructuring-assignment
    props.entityReferencePropertySchema as EntitySelectControlSchema;

  const entityType = useEntityReferenceSchemaType({
    schema: entitySelectSchema,
  });

  const isRelationshipReference = isRelationshipEntityReference(
    entitySelectSchema['x-entity-reference'],
  );

  const Wrapper = isRelationshipReference
    ? PrimitiveObjectArrayItemWithEntityRelationshipWrapper
    : PrimitiveObjectArrayItemWithEntityReferenceWrapper;

  return (
    <EntityLoader
      entityType={entityType}
      render={(entity) => <Wrapper {...props} referencedEntity={entity} />}
    />
  );
};
